import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'sugargoo-buying-guide')!;

export default function BuyingGuide() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: Your First Sugargoo Purchase Made Simple
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          If you have never used a shopping agent before, the process can feel intimidating. Where do you start? How do you find products? What happens after you pay? This <strong>beginner-friendly Sugargoo buying guide</strong> walks you through every step from account creation to package delivery, with clear instructions and practical tips for first-time success.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          By the end of this guide, you will understand the complete workflow, know how to avoid common beginner mistakes, and feel confident placing your first order. Let us turn the unknown into the manageable.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          We recommend reading this entire guide before placing your first order, then bookmarking it to reference during each stage of the process.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 1: Setting Up Your Sugargoo Account
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Before you can buy anything, you need a Sugargoo account. The setup process is straightforward:
        </p>
        <div className="space-y-4">
          {[
            {
              step: 'Register',
              desc: 'Visit the Sugargoo platform and create an account using your email address. Choose a strong password and enable any available security features like two-factor authentication.',
            },
            {
              step: 'Verify Your Account',
              desc: 'Complete any required verification steps. This typically includes email confirmation and may require identity verification depending on your location and order volume.',
            },
            {
              step: 'Add Your Shipping Address',
              desc: 'Enter your complete delivery address including postal code, phone number, and any delivery instructions. Double-check for accuracy — errors here cause delivery failures.',
            },
            {
              step: 'Familiarize Yourself with the Dashboard',
              desc: 'Spend 10 minutes exploring the interface. Understand where to find orders, warehouse items, shipping submissions, and account settings. This small time investment prevents confusion later.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 2: Finding Products to Buy
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The most time-consuming part of agent shopping is finding the right products. Here are the best approaches:
        </p>
        <div className="space-y-4">
          {[
            {
              title: 'Use a Sugargoo Spreadsheet',
              desc: 'The easiest starting point. Organized spreadsheets contain curated product links with quality notes, price references, and direct marketplace URLs. Browse by category, find items you like, and copy the links directly into Sugargoo.',
            },
            {
              title: 'Browse Marketplaces Directly',
              desc: 'Search Taobao, Weidian, or 1688 directly for specific items. Use image search if you have a reference photo. This approach takes longer but offers the widest selection.',
            },
            {
              title: 'Follow Community Recommendations',
              desc: 'Community forums and groups frequently share product finds with reviews and links. These come with built-in quality feedback from real buyers.',
            },
            {
              title: 'Check Seller Ratings',
              desc: 'When browsing marketplaces, pay attention to seller ratings, review counts, and return rates. Higher-rated sellers generally deliver better quality and more reliable service.',
            },
          ].map((item) => (
            <div key={item.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-search-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 3: Submitting Your Order
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Once you have product links, the ordering process works as follows:
        </p>
        <div className="space-y-4">
          {[
            {
              step: 'Paste the Product Link',
              desc: 'Copy the product URL from the marketplace and paste it into Sugargoo order submission form. The system will attempt to fetch product details automatically.',
            },
            {
              step: 'Select Options',
              desc: 'Choose your size, color, and quantity from the available options. If the automatic fetch does not show options, enter them manually in the notes field.',
            },
            {
              step: 'Add Order Notes',
              desc: 'Include any special instructions: &quot;Check for correct logo placement,&quot; &quot;Size XL please,&quot; or &quot;Message seller about stock availability.&quot; Clear notes reduce errors.',
            },
            {
              step: 'Review and Confirm',
              desc: 'Double-check all details before submitting. Verify the price, options, and shipping address. Mistakes at this stage are fixable but cause delays.',
            },
            {
              step: 'Submit and Pay',
              desc: 'Submit the order and complete payment. Sugargoo holds your payment in escrow until the seller ships and the item arrives at the warehouse.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 4: Understanding Payment Methods
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Sugargoo supports multiple payment options. Each has advantages:
        </p>
        <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/60 bg-background-100/50">
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Method</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Speed</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Fees</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Best For</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['PayPal', 'Instant', 'Small fee', 'Buyer protection, easy disputes'],
                ['Credit Card', 'Instant', 'Variable', 'Convenience, rewards points'],
                ['Wise (TransferWise)', '1-2 days', 'Low', 'Lowest fees for bank transfers'],
                ['Crypto', 'Minutes', 'Network fee', 'Privacy, no bank involvement'],
                ['Alipay', 'Instant', 'Low', 'Users with Chinese accounts'],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-foreground-800">{row[0]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[1]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[2]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          PayPal is recommended for beginners because of its buyer protection and dispute resolution. More experienced users may prefer Wise for lower fees on larger amounts.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 5: Warehouse QC and Decision Making
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          After the seller ships your item and it arrives at the Sugargoo warehouse, you will receive QC photos. This is your quality checkpoint:
        </p>
        <div className="space-y-3 mb-4">
          {[
            'Review all QC photos carefully before making any decision',
            'Check for correct size, color, and design against your order',
            'Examine stitching, logos, materials, and overall construction quality',
            'Compare with the seller listing images — significant differences warrant concern',
            'If satisfied, approve the item for storage or shipping',
            'If unsatisfied, request a return or exchange within the allowed timeframe',
          ].map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-check-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <span className="text-sm text-foreground-700">{item}</span>
            </div>
          ))}
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          <strong>Beginner tip:</strong> Do not rush this step. Take your time reviewing photos. Once an item ships internationally, returning it becomes much more complicated and expensive.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 6: Selecting Shipping and Submitting Your Package
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Once all your items have arrived at the warehouse and passed QC, it is time to ship:
        </p>
        <div className="space-y-4">
          {[
            {
              step: 'Select Items to Ship',
              desc: 'Choose which approved items you want to include in your shipment. You can ship everything at once or split into multiple packages.',
            },
            {
              step: 'Choose Shipping Method',
              desc: 'Select from available shipping lines based on your budget and timeline needs. Economy options save money; express options deliver faster.',
            },
            {
              step: 'Choose Packaging Options',
              desc: 'Decide whether to remove boxes, tags, and extra packaging. For fashion items, removal is usually recommended to reduce weight and cost.',
            },
            {
              step: 'Declare Value',
              desc: 'Enter an accurate declared value for customs. Under-declaring risks seizure; over-declaring triggers unnecessary duties. Be truthful but reasonable.',
            },
            {
              step: 'Apply Coupons (If Available)',
              desc: 'Enter any valid shipping coupon codes before finalizing. This is your last chance to reduce shipping costs.',
            },
            {
              step: 'Pay and Submit',
              desc: 'Review the total including shipping, service fees, and any add-ons. Complete payment to submit your package for international dispatch.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Step 7: Tracking and Receiving Your Package
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          After submission, your package enters the international shipping pipeline:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Monitor tracking through your Sugargoo dashboard and the carrier website',
            'Be patient — international shipping involves multiple handoffs and customs checks',
            'If tracking stalls for more than 2 weeks, contact Sugargoo support for an update',
            'When the package arrives, inspect it immediately and document any damage',
            'If everything looks good, enjoy your new items and plan your next order',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 shrink-0"></i>
              <span className="text-sm text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Most packages arrive without issues. The occasional delay or customs hold is normal and usually resolves within a few days.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Beginner Mistakes to Avoid
        </h2>
        <div className="space-y-3">
          {[
            'Ordering too many items for your first haul — start with 2-3 items to learn the process',
            'Ignoring size charts — Asian sizing often runs smaller than Western standards',
            'Choosing the cheapest shipping without understanding the trade-offs in speed and tracking',
            'Forgetting to remove packaging — this alone can add 30-50% to shipping weight',
            'Not reviewing QC photos carefully — this is your only quality checkpoint before international shipping',
            'Panic-contacting support over normal delays — shipping takes time, especially for economy methods',
          ].map((mistake, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 shrink-0 mt-0.5">
                <i className="ri-error-warning-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <span className="text-sm text-foreground-700">{mistake}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: You Are Ready to Start
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Using Sugargoo for the first time is a learning experience, but it is not rocket science. Follow this guide step by step, start small, and build your confidence with each successful order. The international shopping world opens up access to products, styles, and prices that simply are not available through domestic retailers.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Remember: the key to success is patience, research, and realistic expectations. Use a <strong>Sugargoo Spreadsheet</strong> to streamline product discovery, always review QC photos carefully, consolidate shipments to save money, and choose shipping methods that match your timeline and budget.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Welcome to international shopping. Your first haul is just a few clicks away.
        </p>
      </section>
    </ArticleLayout>
  );
}